/**
 * Evaluator for hangingProtocolsV2 condition trees.
 *
 * Paired with `HpRuleEvaluator.java` in the backend — the two are driven by the
 * same fixture suite (`__tests__/fixtures/ruleEngineCases.json`) so they cannot
 * drift. Change one, change both, and add a case.
 *
 * ## Where the semantics come from
 *
 * MedDream evaluates these trees **server-side**: its client bundle contains no
 * operator literals in any evaluation path. So the semantics below are derived
 * from its *quick-save* code, which generates rules from live viewer state and
 * therefore shows what each operator is meant to express:
 *
 *   ("open_study_description", "match",   <the whole description>)  -> equality
 *   ("modalities_in_open_study", "contain", <one modality>)         -> membership
 *   ("series_modality", "match", "MG")                              -> equality
 *
 * Two things could not be recovered from the client and are flagged UNVERIFIED
 * below: case sensitivity, and how multi-valued attributes are split. Both are
 * options here so they can be corrected without touching call sites once we can
 * compare against the Advance Viewer.
 *
 * ## Evaluation context
 *
 * A flat `{ fieldName: value }` map, so the engine is independent of where
 * values come from (Cornerstone stacks in the viewer, JPA entities on the
 * server). Values may be a string, a number, an array, or a `\`-separated DICOM
 * multi-value string. Absent fields evaluate as empty.
 */

import { INPUT_TYPE, getInputType } from "./fieldRegistry";

/** DICOM value multiplicity separator. */
const VM_SEPARATOR = "\\";

/**
 * Fields that direct the match walk rather than filter it, and are therefore
 * skipped here — MedDream skips the same two in its own evaluator
 * (`isNeedToSkipCondition`).
 *
 * They say which of the series/instances a viewport has ALREADY matched it
 * should land on, so only the walk holds the answer and no context can carry
 * one. Evaluated as ordinary numeric rules they read an absent field and fail,
 * which is not a narrower match but no match at all: every viewport of the
 * shared MG protocols carries `scroll_matched_instances = 1`, so a mammogram
 * planned zero viewports everywhere and the whole group vanished from the
 * viewer's protocol menu.
 *
 * Skipping is the whole of it. Where the value is 1 — which is every use of it
 * we have seen — landing on the first match is already what the walk does; a
 * higher one would need the walk to index into its matches, which it cannot yet
 * do.
 */
export const WALK_DIRECTIVE_FIELDS = new Set(["scroll_matched_series", "scroll_matched_instances"]);

export const DEFAULT_OPTIONS = {
  /**
   * UNVERIFIED. Case-insensitive is the safer default for radiology free text —
   * a series description typed as "Nac" should still match "NAC". DICOM CS
   * values (modality, laterality, view position) are uppercase by convention, so
   * this only affects free-text fields.
   */
  caseInsensitive: true,
  /**
   * UNVERIFIED. Whether a scalar string carrying a DICOM multi-value ("CT\\MR")
   * is split into elements before matching.
   */
  splitMultiValue: true,
  /** Called with a diagnostic string when a rule cannot be evaluated. */
  onWarn: null,
};

/* --------------------------------------------------------------- values */

/** Normalise a context value into the list of elements to test against. */
function toElements(value, options) {
  if (value === null || value === undefined) return [];
  if (Array.isArray(value)) {
    return value.flatMap((v) => toElements(v, options));
  }
  if (typeof value === "number") return [String(value)];
  if (typeof value === "boolean") return [String(value)];

  const text = String(value);
  if (text === "") return [];
  if (options.splitMultiValue && text.includes(VM_SEPARATOR)) {
    return text.split(VM_SEPARATOR).map((s) => s.trim()).filter((s) => s !== "");
  }
  return [text];
}

function fold(text, options) {
  return options.caseInsensitive ? text.toLowerCase() : text;
}

/* ------------------------------------------------------------ operators */

/**
 * Positive string operators. Each is "does ANY element satisfy this?", which is
 * what makes `contain` mean membership on a multi-valued field and substring on
 * a scalar one — the same predicate covers both.
 */
const STRING_PREDICATES = {
  match: (element, needle) => element === needle,
  contain: (element, needle) => element.includes(needle),
  begins_with: (element, needle) => element.startsWith(needle),
  end_with: (element, needle) => element.endsWith(needle),
};

/** Negated forms are strict complements, so an absent field satisfies them. */
const NEGATED = {
  not_match: "match",
  not_contain: "contain",
};

const NUMERIC_PREDICATES = {
  "=": (a, b) => a === b,
  "!=": (a, b) => a !== b,
  ">": (a, b) => a > b,
  "<": (a, b) => a < b,
  ">=": (a, b) => a >= b,
  "<=": (a, b) => a <= b,
};

function warn(options, message) {
  if (typeof options.onWarn === "function") options.onWarn(message);
}

function evaluateNumeric(rule, value, options) {
  const predicate = NUMERIC_PREDICATES[rule.operator];
  if (!predicate) {
    warn(options, `unsupported numeric operator "${rule.operator}" on ${rule.field}`);
    return false;
  }
  const expected = Number(rule.value);
  if (Number.isNaN(expected)) {
    warn(options, `non-numeric value "${rule.value}" on ${rule.field}`);
    return false;
  }
  const elements = toElements(value, options);
  if (elements.length === 0) return false;
  // Any element satisfying it is enough, matching the string operators.
  return elements.some((element) => {
    const actual = Number(element);
    return !Number.isNaN(actual) && predicate(actual, expected);
  });
}

function evaluateDatetime(rule, value, options) {
  if (rule.operator !== "<=") {
    warn(options, `unsupported datetime operator "${rule.operator}" on ${rule.field}`);
    return false;
  }
  const elements = toElements(value, options);
  if (elements.length === 0) return false;
  const expected = Date.parse(rule.value);
  if (Number.isNaN(expected)) {
    warn(options, `unparseable datetime "${rule.value}" on ${rule.field}`);
    return false;
  }
  return elements.some((element) => {
    const actual = Date.parse(element);
    return !Number.isNaN(actual) && actual <= expected;
  });
}

function evaluateString(rule, value, options) {
  const negated = NEGATED[rule.operator];
  const positiveOperator = negated || rule.operator;
  const predicate = STRING_PREDICATES[positiveOperator];
  if (!predicate) {
    warn(options, `unsupported string operator "${rule.operator}" on ${rule.field}`);
    return false;
  }
  const needle = fold(String(rule.value ?? ""), options);
  const elements = toElements(value, options).map((e) => fold(e, options));
  const positive = elements.some((element) => predicate(element, needle));
  return negated ? !positive : positive;
}

/** Evaluate a single leaf rule against the context. */
export function evaluateRule(rule, context, options = DEFAULT_OPTIONS) {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  if (WALK_DIRECTIVE_FIELDS.has(rule.field)) return true;
  const value = context ? context[rule.field] : undefined;

  // The rule's own inputType wins: it is what MedDream persisted, and the
  // registry is only a fallback for fields we do not know.
  const inputType = rule.inputType || getInputType(rule.field);

  if (inputType === INPUT_TYPE.NUMERIC) return evaluateNumeric(rule, value, opts);
  if (inputType === INPUT_TYPE.DATETIME) return evaluateDatetime(rule, value, opts);
  return evaluateString(rule, value, opts);
}

/* --------------------------------------------------------------- groups */

const isRuleGroup = (node) => node && typeof node === "object" && Array.isArray(node.rules);

/**
 * Evaluate a rule group.
 *
 * `rules` is an infix array — operands at even indices, "and"/"or" separators at
 * odd ones. MedDream's UI makes the combinator a per-group toggle, so a
 * well-formed group is uniform; mixed combinators are still handled by giving
 * "and" the tighter binding, matching the DSL grammar.
 *
 * An empty group is vacuously true: that is what an untouched condition slot
 * means, and it is the same thing MedDream's `1 == 1` idiom expresses.
 */
export function evaluateRuleGroup(group, context, options = DEFAULT_OPTIONS) {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  if (!isRuleGroup(group)) return true;

  const operands = [];
  const separators = [];
  group.rules.forEach((entry, index) => {
    if (index % 2 === 1) {
      separators.push(entry === "or" ? "or" : "and");
      return;
    }
    operands.push(entry);
  });

  if (operands.length === 0) return !group.not ? true : false;

  // Evaluate the "and" runs first, then "or" across them.
  let orAccumulator = false;
  let andAccumulator = null;

  for (let i = 0; i < operands.length; i += 1) {
    const operand = operands[i];
    const value = isRuleGroup(operand)
      ? evaluateRuleGroup(operand, context, opts)
      : evaluateRule(operand, context, opts);

    andAccumulator = andAccumulator === null ? value : andAccumulator && value;

    const separator = separators[i];
    if (separator === "or" || separator === undefined) {
      orAccumulator = orAccumulator || andAccumulator;
      andAccumulator = null;
    }
  }

  const result = orAccumulator;
  return group.not ? !result : result;
}

/**
 * Evaluate a list of rule groups, as `priorStudyConditions` and
 * `otherStudyConditions` hold. Each entry describes one prior/other study, so
 * the list is a disjunction: an empty list places no constraint.
 */
export function evaluateRuleGroupList(groups, context, options = DEFAULT_OPTIONS) {
  if (!Array.isArray(groups) || groups.length === 0) return true;
  return groups.some((group) => evaluateRuleGroup(group, context, options));
}
